import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getChatService } from '../service/ai-cio-service';

/** Hard cap so a forgotten recording can't grow unbounded (FRONTEND_INTEGRATION §7). */
const MAX_RECORDING_MS = 5 * 60 * 1000;

function pickMimeType(): string {
  if (typeof MediaRecorder === 'undefined') return '';
  if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) return 'audio/webm;codecs=opus';
  if (MediaRecorder.isTypeSupported('audio/webm')) return 'audio/webm';
  if (MediaRecorder.isTypeSupported('audio/mp4')) return 'audio/mp4'; // Safari
  return '';
}

export type VoiceStatus = 'idle' | 'recording' | 'transcribing';

interface UseVoiceInput {
  status: VoiceStatus;
  error: string | null;
  /** Toggles recording; on stop, transcribes and returns text via `onResult`. */
  toggle: () => void;
  /**
   * Instantaneous mic loudness in [0, 1], sampled live from a Web Audio
   * AnalyserNode. Returns 0 when not recording. Safe to poll from a rAF loop —
   * it reads a ref and never triggers a re-render. Drives the recording waveform.
   */
  getLevel: () => number;
}

/**
 * Mic → MediaRecorder → orchestrator /transcribe → text. The transcript is
 * handed back via `onResult` (the composer appends it to the input — the user
 * still presses Send). No streaming/partial transcript (one blob → one text).
 *
 * Also taps the same mic stream with a Web Audio AnalyserNode and exposes
 * `getLevel()` so the composer can draw a live recording waveform.
 */
export function useVoiceInput(onResult: (text: string) => void): UseVoiceInput {
  const { t } = useTranslation('ai-cio');
  const [status, setStatus] = useState<VoiceStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const autoStopRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Web Audio analyser for the live waveform — reads loudness off the same mic
  // stream without touching the recorded blob.
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const timeDataRef = useRef<Uint8Array<ArrayBuffer> | null>(null);

  const releaseAnalyser = useCallback(() => {
    try {
      sourceRef.current?.disconnect();
    } catch {
      // already disconnected
    }
    sourceRef.current = null;
    analyserRef.current = null;
    timeDataRef.current = null;
    const ctx = audioCtxRef.current;
    audioCtxRef.current = null;
    if (ctx && ctx.state !== 'closed') {
      ctx.close().catch(() => {
        // best-effort teardown
      });
    }
  }, []);

  /** Wire an AnalyserNode onto the live stream so getLevel() can sample it. */
  const setupAnalyser = useCallback(
    (stream: MediaStream) => {
      const w =
        typeof window !== 'undefined'
          ? (window as unknown as {
              AudioContext?: typeof AudioContext;
              webkitAudioContext?: typeof AudioContext;
            })
          : undefined;
      const Ctx = w?.AudioContext ?? w?.webkitAudioContext;
      if (!Ctx) return; // No Web Audio → the waveform stays at idle dots.
      try {
        const ctx = new Ctx();
        // Autoplay policy can hand back a suspended context even inside the
        // mic-permission gesture; resume so the analyser produces real samples.
        if (ctx.state === 'suspended') {
          ctx.resume().catch(() => {
            // if it can't resume, the waveform stays at idle dots
          });
        }
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 1024;
        // Low smoothing → the meter tracks per-syllable loudness sharply.
        analyser.smoothingTimeConstant = 0.2;
        source.connect(analyser);
        audioCtxRef.current = ctx;
        sourceRef.current = source;
        analyserRef.current = analyser;
        timeDataRef.current = new Uint8Array(analyser.fftSize);
      } catch {
        releaseAnalyser();
      }
    },
    [releaseAnalyser],
  );

  const getLevel = useCallback((): number => {
    const analyser = analyserRef.current;
    const data = timeDataRef.current;
    if (!analyser || !data) return 0;
    // Time-domain RMS tracks speech amplitude far more sharply than averaged
    // frequency energy — a per-syllable response.
    analyser.getByteTimeDomainData(data);
    let sum = 0;
    for (let i = 0; i < data.length; i += 1) {
      const v = ((data[i] ?? 128) - 128) / 128; // [-1, 1] around silence
      sum += v * v;
    }
    const rms = Math.sqrt(sum / data.length); // 0..~1, but speech is small
    const NOISE_FLOOR = 0.02;
    if (rms <= NOISE_FLOOR) return 0;
    // Gain + soft knee (sqrt) so normal speech lands mid-range and loud peaks
    // reach the top, reacting per syllable rather than pinning to the max.
    return Math.min(1, Math.sqrt((rms - NOISE_FLOOR) * 8));
  }, []);

  const cleanup = useCallback(() => {
    if (autoStopRef.current) {
      clearTimeout(autoStopRef.current);
      autoStopRef.current = null;
    }
    releaseAnalyser();
    streamRef.current?.getTracks().forEach((t) => {
      t.stop();
    });
    streamRef.current = null;
    recorderRef.current = null;
  }, [releaseAnalyser]);

  const stop = useCallback(() => {
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
  }, []);

  const start = useCallback(async () => {
    setError(null);
    const mimeType = pickMimeType();
    if (!mimeType) {
      setError(t('voice.notSupported', 'Voice input is not supported in this browser'));
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      setupAnalyser(stream);
      const recorder = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        cleanup();
        if (!blob.size) {
          setStatus('idle');
          return;
        }
        setStatus('transcribing');
        const service = getChatService();
        if (!service.transcribe) {
          setStatus('idle');
          setError(t('voice.notAvailable', 'Voice input is not available'));
          return;
        }
        service
          .transcribe(blob)
          .then((text) => {
            if (text.trim()) onResult(text.trim());
            else setError(t('voice.noSpeech', 'No speech detected'));
          })
          .catch(() => {
            setError(t('voice.failed', 'Transcription failed, please try again'));
          })
          .finally(() => {
            setStatus('idle');
          });
      };

      recorder.start();
      recorderRef.current = recorder;
      setStatus('recording');
      autoStopRef.current = setTimeout(stop, MAX_RECORDING_MS);
    } catch {
      cleanup();
      setStatus('idle');
      setError(t('voice.micDenied', 'Microphone access denied'));
    }
  }, [cleanup, onResult, setupAnalyser, stop, t]);

  const toggle = useCallback(() => {
    if (status === 'recording') stop();
    else if (status === 'idle') void start();
  }, [status, start, stop]);

  return { status, error, toggle, getLevel };
}
